import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Terminal, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created! You got 100 free tokens.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="register-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(6,182,212,0.08)_0%,rgba(5,5,5,0)_50%)]" />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center text-sm text-white/40 hover:text-white/70 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to home
        </Link>
        <Card className="bg-white/[0.03] border-white/10 backdrop-blur-xl" data-testid="register-card">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Terminal className="w-7 h-7 text-indigo-400" />
              <span className="text-xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>BuilderShift.ai</span>
            </div>
            <CardTitle className="text-2xl text-white">Create account</CardTitle>
            <CardDescription className="text-white/40">Start building with 100 free tokens</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-white/60 text-sm">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-black/50 border-white/10 focus:border-indigo-500/50 text-white mt-1.5" placeholder="Your name" required data-testid="register-name-input" />
              </div>
              <div>
                <Label className="text-white/60 text-sm">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-black/50 border-white/10 focus:border-indigo-500/50 text-white mt-1.5" placeholder="you@example.com" required data-testid="register-email-input" />
              </div>
              <div>
                <Label className="text-white/60 text-sm">Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-black/50 border-white/10 focus:border-indigo-500/50 text-white mt-1.5" placeholder="Min 6 characters" required data-testid="register-password-input" />
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-white h-10 shadow-[0_0_20px_rgba(99,102,241,0.3)]" disabled={loading} data-testid="register-submit-btn">
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
            <p className="text-center text-sm text-white/30 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-indigo-400 hover:text-indigo-300" data-testid="register-login-link">Sign in</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
